import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, RefreshCw, LogOut, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const TableSelectionPage = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArea, setSelectedArea] = useState('all');
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');

  useEffect(() => {
    // Check authentication and restaurant selection
    const token = localStorage.getItem('staffToken');
    const selectedRestId = localStorage.getItem('selectedRestaurantId');
    const selectedRestName = localStorage.getItem('selectedRestaurantName');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    if (!selectedRestId) {
      navigate('/select-restaurant');
      return;
    }
    
    setRestaurantId(parseInt(selectedRestId));
    setRestaurantName(selectedRestName || 'Restaurant');
    fetchTables(selectedRestId);
  }, [navigate]);

  const fetchActiveOrderForTable = async (restId, tableId, token) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/billing-pos/restaurants/${restId}/dine-in/tables-by-id/${tableId}/active-order`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        return { hasActiveOrder: false };
      }

      const data = await response.json();
      if (data?.hasActiveOrder && data?.orderId) {
        return {
          hasActiveOrder: true,
          activeOrderId: data.orderId,
          activeOrderNumber: data.billNo || data.orderNumber || `ORD-${data.orderId}`,
          activeOrderTotal: data.grandTotal ?? null,
          activeOrderStatus: data.orderStatus || 'RUNNING'
        };
      }

      return { hasActiveOrder: false };
    } catch (error) {
      console.log(`Active order check failed for table ${tableId}:`, error.message);
      return { hasActiveOrder: false };
    }
  };

  const enrichTablesWithActiveOrders = async (restId, tablesList, token) => {
    const enrichedTables = await Promise.all(
      tablesList.map(async (table) => {
        const activeOrder = await fetchActiveOrderForTable(restId, table.tableId, token);
        return {
          ...table,
          ...activeOrder
        };
      })
    );

    return enrichedTables;
  };

  const fetchTables = async (restId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('staffToken');
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/billing-pos/restaurants/${restId}/areas/all-tables`;
      
      console.log('🔗 Fetching tables from:', apiUrl);
      
      // Try the billing POS API first (more comprehensive)
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Tables data received:', data);
        
        let tablesList = [];
        let areasList = [];
        
        // Handle both response formats
        if (Array.isArray(data)) {
          // Old format: array of areas with nested tables
          data.forEach(area => {
            areasList.push({ areaId: area.areaId, areaName: area.areaName });
            if (area.tables && Array.isArray(area.tables)) {
              area.tables.forEach(table => {
                tablesList.push({
                  tableId: table.tableId,
                  tableNumber: table.tableNumber,
                  areaId: area.areaId,
                  status: table.status || 'available',
                  capacity: table.seatingCapacity
                });
              });
            }
          });
        } else if (data.areas && data.tables) {
          // New format: {areas: [], tables: []}
          areasList = data.areas || [];
          tablesList = data.tables || [];
        } else if (data.tables) {
          // Just tables array
          tablesList = data.tables;
          areasList = data.areas || [];
        }
        
        const enrichedTables = await enrichTablesWithActiveOrders(restId, tablesList, token);

        console.log('📋 Parsed tables:', tablesList);
        console.log('🧾 Enriched tables with active orders:', enrichedTables);
        console.log('📍 Parsed areas:', areasList);
        
        setTables(enrichedTables);
        setAreas(areasList);
      } else {
        toast.error('Failed to load tables');
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast.error('Failed to load tables');
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = (table) => {
    // Navigate to menu order page with table info (r=restaurantId, t=tableId)
    navigate(`/menu?r=${restaurantId}&t=${table.tableId}&tableNumber=${table.tableNumber}`);
  };

  const handleBackToRestaurants = () => {
    navigate('/select-restaurant');
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const filteredTables = tables.filter(table => {
    const matchesSearch = table.tableNumber.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesArea = selectedArea === 'all' || table.areaId === parseInt(selectedArea);
    return matchesSearch && matchesArea;
  });

  const getTableStatus = (table) => {
    if (table.hasActiveOrder) {
      return { color: 'bg-orange-500', text: 'Running' };
    }

    if (table.status === 'occupied' || table.status === 'running') {
      return { color: 'bg-orange-500', text: 'Running' };
    } else if (table.status === 'saved') {
      return { color: 'bg-yellow-500', text: 'Saved' };
    } else if (table.status === 'printed') {
      return { color: 'bg-blue-500', text: 'Bill Printed' };
    }
    return { color: 'bg-green-500', text: 'Available' };
  };

  const getActiveOrderMeta = (table) => {
    const orderNumber =
      table.activeOrderNumber ||
      table.orderNumber ||
      table.billNo ||
      table.runningOrderNumber ||
      table.currentOrderNumber;

    const rawAmount =
      table.activeOrderTotal ??
      table.grandTotal ??
      table.totalAmount ??
      table.runningOrderTotal;

    const amount = Number(rawAmount);
    const hasAmount = !Number.isNaN(amount) && amount > 0;

    return {
      orderNumber,
      amount: hasAmount ? amount : null
    };
  };

  const shouldShowActiveOrderMeta = (table, orderMeta) => {
    if (table.hasActiveOrder) {
      return true;
    }

    const isActiveStatus = ['occupied', 'running', 'saved', 'printed'].includes((table.status || '').toLowerCase());
    return isActiveStatus && (orderMeta.orderNumber || orderMeta.amount !== null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <button
                onClick={handleBackToRestaurants}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                title="Back to restaurants"
              >
                <ArrowLeft size={18} />
              </button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-800 leading-tight truncate">{restaurantName}</h1>
                <p className="text-xs text-gray-600">Select a table to take order</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchTables(restaurantId)}
                className="p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                title="Refresh tables"
              >
                <RefreshCw size={18} />
              </button>
              <button
                onClick={handleLogout}
                className="p-1.5 bg-red-600 text-white rounded-md hover:bg-red-700"
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-8 sm:col-span-9 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search table..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="col-span-4 sm:col-span-3 w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Areas</option>
              {areas.map(area => (
                <option key={area.areaId} value={area.areaId}>
                  {area.areaName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredTables.length === 0 ? (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No tables found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredTables.map(table => {
              const status = getTableStatus(table);
              const orderMeta = getActiveOrderMeta(table);
              const showActiveOrderMeta = shouldShowActiveOrderMeta(table, orderMeta);
              return (
                <button
                  key={table.tableId}
                  onClick={() => handleTableSelect(table)}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-6 border-2 border-transparent hover:border-blue-500"
                >
                  <div className="text-center">
                    {showActiveOrderMeta && (
                      <div className="mb-2 rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-left">
                        <p className="text-[11px] font-semibold text-orange-700 truncate">
                          Active Order: {orderMeta.orderNumber || 'Running'}
                        </p>
                        {orderMeta.amount !== null && (
                          <p className="text-[11px] font-semibold text-orange-800">Total: Rs {orderMeta.amount.toFixed(1)}</p>
                        )}
                      </div>
                    )}
                    <div className={`w-16 h-16 ${status.color} rounded-full mx-auto mb-3 flex items-center justify-center`}>
                      <span className="text-2xl font-bold text-white">{table.tableNumber}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Table {table.tableNumber}</p>
                    <p className="text-xs text-gray-500 mt-1">{status.text}</p>
                    {table.capacity && (
                      <p className="text-xs text-gray-400 mt-1">
                        <Users size={12} className="inline mr-1" />
                        {table.capacity} seats
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TableSelectionPage;
